import { useMemo, useState } from 'react';
import { ShieldAlert, Plus, RotateCw, ChevronDown } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
  Switch,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from '../WaapEmptyCard';
import {
  useWaapThreatDetectionCharts,
  API_PROTECTION_TIME_RANGES,
  type ApiProtectionTimeRange,
  useWaapThreats,
  useWaapThreatRules,
  useCreateWaapThreatRule,
  useUpdateWaapThreatRule,
  useDeleteWaapThreatRule,
  useWaapDataLossApiAppOptions,
  useWaapDataLossApiAssetOptions,
  type WaapThreatRule,
  type WaapThreatRuleConfig,
  type WaapThreatCondition,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { useWaapUpgradeGate } from '@/pages/services/waap/use-waap-upgrade-gate';
import { WaapChartWidget } from '../dashboard/WaapChartWidget';
import styles from './ApiProtectionThreatDetectionPage.module.css';
import grid from './ApiProtectionPage.module.css';

const PAGE_SIZE = 25;

const ALL_WEEKS = ['1', '2', '3', '4', '5', '6', '7'];

const RISK_LEVELS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];
// Source selectors. type=ip confirmed; action options Region/Multiple IP/IP Range
// confirmed from the portal (values multiple_ip/ip_range are best-guess pending a
// payload). value depends on action: region → "all" or ISO country code(s);
// multiple_ip → IP list; ip_range → "start-end" (exact encoding TODO via payload).
const SOURCE_TYPES = [{ value: 'ip', label: 'IP' }];
const SOURCE_ACTIONS = [
  { value: 'region', label: 'Region' },
  { value: 'multiple_ip', label: 'Multiple IP' },
  { value: 'ip_range', label: 'IP Range' },
];
// Target (vendor target.type). "all" carries value []; app/api carry a list of ids
// (confirmed from a captured payload, e.g. target:{type:"app",value:["247","57"]}) —
// the ids come from the account's app/asset options. "tag" ids source is still TBD.
const TARGETS = [
  { value: 'all', label: 'All' },
  { value: 'api', label: 'API' },
  { value: 'app', label: 'Application' },
  { value: 'tag', label: 'Tag' },
];
// Metric Collection Interval (vendor `stat_period`). once/minute confirmed; day
// is the vendor's third option ("1 day").
const STAT_PERIODS = [
  { value: 'once', label: 'Once' },
  { value: 'minute', label: '1 minute' },
  { value: 'day', label: '1 day' },
];
// Scheduling (vendor access_time.type). "all" = Always; "fixed" = Periodic
// (weekly days + a UTC time window; confirmed from a captured payload). When fixed,
// start_time/end_time are UTC "HH:MM:SS" and config carries a matching time_range.
const SCHEDULES = [
  { value: 'all', label: 'Always' },
  { value: 'fixed', label: 'Periodic' },
];
const WEEKDAYS = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '7', label: 'Sun' },
];
// Triggering-condition metric (vendor `key`). status confirmed; the rest map the
// vendor's Metrics dropdown labels — confirm referer/request_length/response_length
// keys against a captured rule.
const METRICS = [
  { value: 'status', label: 'Status Code' },
  { value: 'referer', label: 'Referer' },
  { value: 'ua', label: 'User Agent' },
  { value: 'http_method', label: 'HTTP Method' },
  { value: 'request_length', label: 'Request Length' },
  { value: 'response_length', label: 'Response Length' },
];
// Matching condition (vendor `operation` / `sql_operation`).
const MATCHES = [
  { value: '==', label: 'Equal to' },
  { value: '!=', label: 'Not equal to' },
  { value: '>', label: 'Greater than' },
  { value: '<', label: 'Less than' },
  { value: '>=', label: 'Equal or greater than' },
  { value: '<=', label: 'Equal or less than' },
];

const riskTone = (v: string) => (v === 'high' ? 'danger' : v === 'medium' ? 'warning' : 'neutral');
const metricLabel = (k: string) => METRICS.find((m) => m.value === k)?.label ?? k;
const matchLabel = (o: string) => MATCHES.find((m) => m.value === o)?.label ?? o;

// A brand-new custom rule. Source defaults to the captured ip/region/all shape;
// Scheduling defaults to Always (every day, no window).
const emptyRule = (): WaapThreatRule => ({
  id: 0,
  name: '',
  type: 2,
  config: {
    enable: true,
    rule_type: 'custom',
    description: '',
    risk_level: { value: 'medium', label: 'Medium' },
    source: { type: 'ip', action: 'region', value: 'all' },
    target: { type: 'all', value: [] },
    stat_period: 'once',
    access_time: { type: 'all', value: { weeks: [...ALL_WEEKS], start_time: '', end_time: '' } },
    rules: [],
  },
});

const emptyCond = (): WaapThreatCondition => ({
  key: 'status',
  label: 'status',
  operation: '==',
  sql_operation: '==',
  value: '',
});

// --- Periodic scheduling time conversion --------------------------------------
// The vendor stores the Periodic window in UTC: access_time.value.start_time /
// end_time are UTC "HH:MM:SS" and config.time_range is [startISO, endISO] (UTC,
// today's date). The form edits in the viewer's LOCAL time, so convert on the way
// out (local→UTC) and back on edit (UTC→local). Matches captured portal payloads.
const pad2 = (n: number) => String(n).padStart(2, '0');
/** local "HH:MM[:SS]" → { time: UTC "HH:MM:SS", iso: today's UTC ISO }. */
function localTimeToUtc(local: string): { time: string; iso: string } {
  if (!local) return { time: '', iso: '' };
  const [h, m, s = 0] = local.split(':').map((x) => parseInt(x, 10) || 0);
  const d = new Date();
  d.setHours(h, m, s, 0);
  const iso = d.toISOString();
  return { time: iso.slice(11, 19), iso };
}
/** UTC "HH:MM:SS" → local "HH:MM:SS" for display in the time inputs. */
function utcTimeToLocal(utc: string): string {
  if (!utc) return '';
  const [h, m, s = 0] = utc.split(':').map((x) => parseInt(x, 10) || 0);
  const d = new Date();
  d.setUTCHours(h, m, s, 0);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}
/** Form config → vendor body: for Periodic (fixed), UTC-ify the window + attach
 *  time_range; for Always, strip any stale time_range. */
function toVendorConfig(config: WaapThreatRuleConfig): WaapThreatRuleConfig {
  const out: WaapThreatRuleConfig = { ...config, description: config.description.trim() };
  if (config.access_time.type === 'fixed') {
    const st = localTimeToUtc(config.access_time.value.start_time);
    const et = localTimeToUtc(config.access_time.value.end_time);
    out.access_time = {
      ...config.access_time,
      value: { ...config.access_time.value, start_time: st.time, end_time: et.time },
    };
    out.time_range = [st.iso, et.iso];
  } else {
    delete out.time_range;
  }
  return out;
}
/** Vendor rule → form config: convert a Periodic window's UTC times back to local. */
function fromVendorConfig(config: WaapThreatRuleConfig): WaapThreatRuleConfig {
  if (config.access_time?.type !== 'fixed') return config;
  return {
    ...config,
    access_time: {
      ...config.access_time,
      value: {
        ...config.access_time.value,
        start_time: utcTimeToLocal(config.access_time.value.start_time),
        end_time: utcTimeToLocal(config.access_time.value.end_time),
      },
    },
  };
}

/**
 * Overview — the Threat Detection BI board (Superset 61) rendered as NATIVE charts
 * (no iframe; matches the sibling Management Overview). The BE returns the widgets
 * in the vendor's display order; we render them in ONE 3-col grid with the wide
 * Threats Trend spanning the full width, so the KPIs + pie + Top-10 tables tile
 * cleanly (no dangling empty cell). The Time Range is the dashboard's "time_range"
 * filter; Refresh re-fetches the board.
 */
function OverviewTab({ domainId }: { domainId: string }) {
  const [range, setRange] = useState<ApiProtectionTimeRange>('Last 24 Hours');
  const { data, isLoading, isError, error, refetch, isFetching } = useWaapThreatDetectionCharts(
    domainId,
    range,
  );
  const charts = data?.charts ?? [];

  return (
    <>
      <div className={grid.toolbar}>
        <span className={grid.rangeLabel}>Time Range</span>
        <Select value={range} onValueChange={(v) => setRange(v as ApiProtectionTimeRange)}>
          <SelectTrigger className={grid.rangeSelect}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {API_PROTECTION_TIME_RANGES.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          disabled={isFetching}
          className={grid.refreshBtn}
        >
          <RotateCw size={14} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <Skeleton height={480} />
      ) : data === null ? (
        <WaapEmptyCard
          icon={<ShieldAlert size={20} />}
          title="Threat Detection analytics not configured"
          description="The analytics board is not set up for this environment yet."
        />
      ) : isError ? (
        <Alert tone="danger" title="Couldn't load Threat Detection overview">
          {errMessage(error)}
        </Alert>
      ) : (
        <div className={grid.overviewGrid}>
          {charts.map((c) => (
            <div key={c.key} className={c.kind === 'timeseries' ? grid.cellFull : grid.cell}>
              <WaapChartWidget chart={c} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Threats — paginated list of detected API threats. */
function ThreatsTab({ domainId }: { domainId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useWaapThreats(domainId, { page, length: PAGE_SIZE });

  // Item keys are best-effort: the live account returned an empty list, so each
  // column reads the first present candidate key. Adjust once real rows appear.
  const cell = (row: Record<string, unknown>, keys: string[]) => {
    for (const k of keys) {
      const v = row[k];
      if (v != null && v !== '') return String(v);
    }
    return '—';
  };
  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      { id: 'start_time', header: 'Start Time', cell: ({ row }) => cell(row.original, ['start_time', 'startTime', 'time', 'timestamp']) },
      { id: 'application', header: 'Application', cell: ({ row }) => cell(row.original, ['application', 'app_name', 'app']) },
      { id: 'api_path', header: 'API Path', cell: ({ row }) => cell(row.original, ['api_path', 'apiPath', 'path', 'api_endpoint']) },
      { id: 'risk_level', header: 'Risk Level', cell: ({ row }) => cell(row.original, ['risk_level', 'riskLevel', 'risk']) },
      { id: 'source_ip', header: 'Source IP', cell: ({ row }) => cell(row.original, ['source_ip', 'sourceIp', 'ip']) },
      { id: 'threat', header: 'Threat', cell: ({ row }) => cell(row.original, ['threat', 'threat_name']) },
      { id: 'threat_type', header: 'Threat Type', cell: ({ row }) => cell(row.original, ['threat_type', 'threatType', 'type']) },
      { id: 'threat_category', header: 'Threat Category', cell: ({ row }) => cell(row.original, ['threat_category', 'threatCategory', 'category']) },
    ],
    [],
  );

  const rows = data?.list ?? [];
  const pageCount = Math.max(1, data?.totalPageNumber ?? 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Threats</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton height={320} />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            emptyState={
              <EmptyState
                size="sm"
                icon={<ShieldAlert size={20} />}
                title="No API threats"
                description="No threats match the current window."
              />
            }
            footer={<Pagination page={page} pageCount={pageCount} onPageChange={setPage} />}
          />
        )}
      </CardBody>
    </Card>
  );
}

/** Detection Rules — custom rules list + add/edit (inline) + delete. */
function DetectionRulesTab({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const { data, isLoading } = useWaapThreatRules(domainId);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<WaapThreatRule>(emptyRule());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WaapThreatRule | null>(null);
  const [cond, setCond] = useState<WaapThreatCondition>(emptyCond());
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Target id options (Application / API). Account-wide, so reuse the Data Loss
  // app/asset option endpoints — they return {label: domain, value: id} exactly
  // as the vendor target.value expects (e.g. ["247","57"]).
  const appOpts = useWaapDataLossApiAppOptions(domainId);
  const assetOpts = useWaapDataLossApiAssetOptions(domainId);
  const targetOpts =
    form.config.target.type === 'app' ? appOpts.data
    : form.config.target.type === 'api' ? assetOpts.data
    : [];

  // Custom Rules table shows only custom (type 2); system presets (type 1, e.g.
  // "IP Flooding") are hidden, matching the vendor portal.
  const rules = useMemo(() => (data ?? []).filter((r) => r.type === 2), [data]);

  const close = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyRule());
    setCond(emptyCond());
  };
  const startAdd = () => {
    setEditingId(null);
    setForm(emptyRule());
    setCond(emptyCond());
    setOpen(true);
  };
  const startEdit = (r: WaapThreatRule) => {
    setEditingId(r.id);
    setForm({ ...r, config: fromVendorConfig({ ...r.config, rules: [...r.config.rules] }) });
    setOpen(true);
  };

  const create = useCreateWaapThreatRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Detection rule created' }); close(); },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateWaapThreatRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Detection rule updated' }); close(); },
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const remove = useDeleteWaapThreatRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Detection rule deleted' }); setPendingDelete(null); },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });
  // Inline enable/disable: a separate mutation (own toast + per-row pending) so
  // it doesn't drive the edit form's saving state. Toggling is a whole-object PUT
  // with config.enable flipped — the rule is round-tripped intact (created /
  // status / customer_id preserved by spreading the fetched row).
  const toggle = useUpdateWaapThreatRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Status updated' }); setTogglingId(null); },
    onError: (e) => { show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }); setTogglingId(null); },
  });
  const toggleEnabled = (r: WaapThreatRule, enable: boolean) => {
    setTogglingId(r.id);
    toggle.mutate({ id: r.id, body: { ...r, config: { ...r.config, enable } } });
  };

  const patchConfig = (patch: Partial<WaapThreatRule['config']>) =>
    setForm((f) => ({ ...f, config: { ...f.config, ...patch } }));

  // Scheduling: Always (type "all", every day) vs Periodic (type "fixed",
  // chosen weekdays + time window).
  const setSchedule = (type: string) =>
    patchConfig({
      access_time:
        type === 'all'
          ? { type: 'all', value: { weeks: [...ALL_WEEKS], start_time: '', end_time: '' } }
          : { type: 'fixed', value: { ...form.config.access_time.value } },
    });
  const toggleWeek = (d: string, on: boolean) => {
    const cur = form.config.access_time.value.weeks;
    const weeks = on ? [...cur, d].filter((v, i, a) => a.indexOf(v) === i).sort() : cur.filter((w) => w !== d);
    patchConfig({ access_time: { ...form.config.access_time, value: { ...form.config.access_time.value, weeks } } });
  };
  const setTime = (k: 'start_time' | 'end_time', v: string) =>
    patchConfig({ access_time: { ...form.config.access_time, value: { ...form.config.access_time.value, [k]: v } } });

  // Switching the Source action resets value to that action's shape (region → "all").
  const setSourceAction = (action: string) =>
    patchConfig({ source: { ...form.config.source, action, value: action === 'region' ? 'all' : '' } });
  const sourceValuePlaceholder =
    form.config.source.action === 'multiple_ip'
      ? 'IPs, comma-separated'
      : form.config.source.action === 'ip_range'
        ? 'startIP-endIP'
        : 'all or ISO code(s) e.g. VN,SG';

  const addCondition = () => {
    const raw = String(cond.value).trim();
    if (!raw) return;
    const num = Number(raw);
    const value: number | string = Number.isFinite(num) ? num : raw;
    // The portal echoes label == key for custom rules (see captured payloads).
    const next: WaapThreatCondition = { ...cond, label: cond.key, sql_operation: cond.operation, value };
    setForm((f) => ({ ...f, config: { ...f.config, rules: [...f.config.rules, next] } }));
    setCond(emptyCond());
  };
  const removeCondition = (i: number) =>
    setForm((f) => ({ ...f, config: { ...f.config, rules: f.config.rules.filter((_, idx) => idx !== i) } }));

  // app/api targets must name at least one id; "all"/"tag" don't.
  const targetOk =
    form.config.target.type === 'all' ||
    form.config.target.type === 'tag' ||
    form.config.target.value.length > 0;
  const valid = form.name.trim().length > 0 && form.config.rules.length > 0 && targetOk;
  const saving = create.isPending || update.isPending;
  const submit = () => {
    const body: WaapThreatRule = {
      ...form,
      name: form.name.trim(),
      config: toVendorConfig(form.config),
    };
    if (editingId) update.mutate({ id: editingId, body });
    else create.mutate(body);
  };

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Plus size={14} />}
            onClick={gate('apiProtection', 'API Protection', startAdd)}
          >
            Add Custom Rule
          </Button>
        }
      >
        <CardTitle>Custom Rules</CardTitle>
      </CardHeader>
      <CardBody className={styles.bodyGrid}>
        {isLoading ? (
          <Skeleton height={80} />
        ) : rules.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<ShieldAlert size={20} />}
            title="No detection rules"
            description="No custom threat-detection rules on this website yet."
          />
        ) : (
          <div className={styles.rulesScroll}><div className={styles.rules}>
            <div className={styles.ruleHead}>
              <span>Threat</span>
              <span>Risk Level</span>
              <span>Description</span>
              <span>Status</span>
              <span>Operation</span>
            </div>
            {rules.map((r) => (
              <div key={r.id} className={styles.ruleRow}>
                <span className={styles.name}>{r.name}</span>
                <span>
                  <StatusPill tone={riskTone(r.config.risk_level.value)}>{r.config.risk_level.label}</StatusPill>
                </span>
                <span className={styles.muted}>{r.config.description || '—'}</span>
                <span className={styles.statusCell}>
                  <Switch
                    size="sm"
                    checked={r.config.enable}
                    disabled={togglingId === r.id}
                    onCheckedChange={(v) => toggleEnabled(r, Boolean(v))}
                    aria-label={r.config.enable ? 'Disable rule' : 'Enable rule'}
                  />
                  <span className={r.config.enable ? undefined : styles.muted}>
                    {r.config.enable ? 'Enabled' : 'Disabled'}
                  </span>
                </span>
                <div className={styles.actions}>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => setPendingDelete(r)}>Delete</Button>
                </div>
              </div>
            ))}
          </div></div>
        )}

        {open && (
          <div className={styles.form}>
            <Field label="Name" required description="1-26 chars: 0-9 A-Z a-z space . _ - ( ) * +">
              {(p) => <Input {...p} value={form.name} maxLength={26} placeholder="Monitor_404_Errors" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />}
            </Field>
            <Field label="Description" description="Up to 200 characters.">
              {(p) => <Input {...p} value={form.config.description} maxLength={200} onChange={(e) => patchConfig({ description: e.target.value })} />}
            </Field>

            <div className={styles.fieldRow}>
              <Field label="Risk Level" required>
                {() => (
                  <Select
                    value={form.config.risk_level.value}
                    onValueChange={(v) => patchConfig({ risk_level: { value: v, label: RISK_LEVELS.find((r) => r.value === v)?.label ?? v } })}
                  >
                    <SelectTrigger className={styles.selectMd}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RISK_LEVELS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Metric Collection Interval" required>
                {() => (
                  <Select value={form.config.stat_period} onValueChange={(v) => patchConfig({ stat_period: v })}>
                    <SelectTrigger className={styles.selectMd}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STAT_PERIODS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            <Field label="Source" required>
              {() => (
                <div className={styles.sourceRow}>
                  <Select value={form.config.source.type} onValueChange={(v) => patchConfig({ source: { ...form.config.source, type: v } })}>
                    <SelectTrigger className={styles.sourceSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCE_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={form.config.source.action ?? 'region'} onValueChange={setSourceAction}>
                    <SelectTrigger className={styles.sourceSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCE_ACTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input
                    className={styles.condValue}
                    placeholder={sourceValuePlaceholder}
                    value={String(form.config.source.value)}
                    onChange={(e) => patchConfig({ source: { ...form.config.source, value: e.target.value } })}
                  />
                </div>
              )}
            </Field>

            <div className={styles.fieldRow}>
              <Field label="Target" required>
                {() => (
                  <div className={styles.sourceRow}>
                    <Select value={form.config.target.type} onValueChange={(v) => patchConfig({ target: { type: v, value: [] } })}>
                      <SelectTrigger className={styles.sourceSelect}><SelectValue /></SelectTrigger>
                      <SelectContent>{TARGETS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {(form.config.target.type === 'app' || form.config.target.type === 'api') && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="secondary"
                            className={styles.condValue}
                            style={{ justifyContent: 'space-between', fontWeight: 'var(--vn-font-weight-regular)' }}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {form.config.target.value.length
                                ? `${form.config.target.value.length} selected`
                                : `Select ${form.config.target.type === 'app' ? 'applications' : 'APIs'}`}
                            </span>
                            <ChevronDown size={14} />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)', maxHeight: 240, overflowY: 'auto' }}>
                            {(targetOpts ?? []).length === 0 ? (
                              <span className={styles.muted}>No options for this account</span>
                            ) : (
                              (targetOpts ?? []).map((o) => {
                                const selected = form.config.target.value.includes(o.value);
                                return (
                                  <label key={o.value} style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
                                    <Checkbox
                                      checked={selected}
                                      onCheckedChange={(v) => {
                                        const next = new Set(form.config.target.value);
                                        if (v) next.add(o.value);
                                        else next.delete(o.value);
                                        patchConfig({ target: { ...form.config.target, value: [...next] } });
                                      }}
                                    />
                                    <span>{o.label}</span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Scheduling" required description="Always = always effective; Periodic = only on the chosen days/time.">
                {() => (
                  <Select value={form.config.access_time.type} onValueChange={setSchedule}>
                    <SelectTrigger className={styles.selectMd}><SelectValue /></SelectTrigger>
                    <SelectContent>{SCHEDULES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            {form.config.access_time.type !== 'all' && (
              <div className={styles.condSection}>
                <div className={styles.weekRow}>
                  {WEEKDAYS.map((d) => (
                    <label key={d.value} className={styles.weekLabel}>
                      <Checkbox
                        checked={form.config.access_time.value.weeks.includes(d.value)}
                        onCheckedChange={(v) => toggleWeek(d.value, Boolean(v))}
                      />
                      {d.label}
                    </label>
                  ))}
                </div>
                <div className={styles.fieldRow}>
                  <Field label="Start time">
                    {(p) => <Input {...p} type="time" step={1} value={form.config.access_time.value.start_time} onChange={(e) => setTime('start_time', e.target.value)} />}
                  </Field>
                  <Field label="End time">
                    {(p) => <Input {...p} type="time" step={1} value={form.config.access_time.value.end_time} onChange={(e) => setTime('end_time', e.target.value)} />}
                  </Field>
                </div>
              </div>
            )}

            <div className={styles.condSection}>
              <div className={styles.sectionLabel}>Triggering Condition</div>
              <div className={styles.condRow}>
                <Select value={cond.key} onValueChange={(v) => setCond((c) => ({ ...c, key: v }))}>
                  <SelectTrigger className={styles.condSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={cond.operation} onValueChange={(v) => setCond((c) => ({ ...c, operation: v }))}>
                  <SelectTrigger className={styles.condSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MATCHES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className={styles.condValue} placeholder="404" value={String(cond.value)} onChange={(e) => setCond((c) => ({ ...c, value: e.target.value }))} />
                <Button variant="secondary" size="sm" onClick={addCondition} disabled={!String(cond.value).trim()}>Add</Button>
              </div>

              {form.config.rules.length > 0 && (
                <div className={styles.condList}>
                  {form.config.rules.map((c, i) => (
                    <div key={i} className={styles.condItem}>
                      <span>{metricLabel(c.key)}</span>
                      <span className={styles.muted}>{matchLabel(c.operation)}</span>
                      <span>{String(c.value)}</span>
                      <div className={styles.actions}>
                        <Button variant="ghost" size="sm" onClick={() => removeCondition(i)}>Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <Button variant="secondary" onClick={close}>Cancel</Button>
              <Button disabled={!valid || saving} onClick={submit}>
                {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </CardBody>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete detection rule?"
        description={`Remove "${pendingDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
      {upgradeDialog}
    </Card>
  );
}

const TABS = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'threats', label: 'Threats' },
  { slug: 'rules', label: 'Detection Rules' },
] as const;

/**
 * API Protection → Threat Detection. Overview (native BI charts, board 61),
 * Threats (paginated list) and Detection Rules (custom rules CRUD), all backed by
 * the private waap2 portal. Account-level: the selected website only resolves the
 * Conversant customer.
 */
export function ApiProtectionThreatDetectionPage() {
  // Account-level: any of the account's websites resolves the same customer, so
  // a representative domain is used automatically (no picker / select-website gate).
  const { selected, isLoading: wsLoading } = useWaapWebsite();
  const domainId = selected?.domainId ?? '';
  const [tab, setTab] = useState<string>('rules');

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'API Protection' }, { label: 'Threat Detection' }]} />}
        title="Threat Detection"
      />

      <Tabs value={tab} onValueChange={setTab} style={{ marginBottom: 'var(--vn-space-4)' }}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {wsLoading ? (
        <Skeleton height={320} />
      ) : tab === 'overview' ? (
        <OverviewTab domainId={domainId} />
      ) : tab === 'threats' ? (
        <ThreatsTab domainId={domainId} />
      ) : (
        <DetectionRulesTab domainId={domainId} />
      )}
    </Container>
  );
}
